import React, { createContext, useState, useEffect, useContext } from 'react';
import { getTheme } from './theme';

// Create the context
const ThemeContext = createContext();

// Helper to convert nested theme object to CSS custom properties
const themeToCSSVariables = (theme, prefix = '') => {
  const variables = {};
  
  for (const [key, value] of Object.entries(theme)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recursively handle nested objects
      Object.assign(variables, themeToCSSVariables(value, `${prefix}${key}-`));
    } else {
      // Convert to CSS variable name
      variables[`--${prefix}${key}`] = value;
    }
  }
  
  return variables;
};

// Theme Provider Component
export function ThemeProvider({ children }) {
  // Get initial theme from localStorage or default to 'light'
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('theme-mode');
    return saved || 'light';
  });
  
  // Get the theme object for current mode
  const theme = getTheme(mode);
  
  // Apply CSS custom properties to :root when theme changes
  useEffect(() => {
    const root = document.documentElement;
    const cssVars = themeToCSSVariables(theme);
    
    // Apply each CSS variable
    Object.entries(cssVars).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Save to localStorage
    localStorage.setItem('theme-mode', mode);

    // Update body class for any legacy styles
    document.body.className = `theme-${mode}`;

    // Sync Tailwind's class-based dark mode (darkMode: 'class') to the app
    // theme, so every `dark:` variant follows the in-app toggle — not the OS
    // prefers-color-scheme. Fixes dark controls rendering on a light page when
    // the user's OS theme differs from their app theme.
    root.classList.toggle('dark', mode === 'dark');
  }, [theme, mode]);
  
  // Toggle between light and dark
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'dark' ? 'light' : 'dark'));
  };
  
  // Set specific theme
  const setTheme = (newMode) => {
    if (newMode === 'light' || newMode === 'dark') {
      setMode(newMode);
    }
  };
  
  const value = {
    mode,
    theme,
    toggleTheme,
    setTheme,
    isDark: mode === 'dark',
    isLight: mode === 'light',
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use theme
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
